import { Box, Button, Drawer, Flex, Loader, Paper, Text } from '@mantine/core';
import { useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { fetchRows, isDebugTab, rowHeadline } from './BankAccountLifecycleDebugDrawer.utils.ts';
import type { DebugRow, DebugTab } from './BankAccountLifecycleDebugDrawer.types.ts';

// Debug-only tooling for the bank account lifecycle (MIP-2656): shows the raw
// validation logs and state transitions of one account via the superadmin debug
// endpoints. Deliberately untranslated and self-contained — delete this folder (plus
// its BankAccountSection mount and the debug controller) to remove the tool.

const RowList = ({ query }: { query: UseQueryResult<DebugRow[]> }) => {
	if (query.isLoading) {
		return <Loader size="sm" />;
	}
	if (query.isError) {
		return <Text c="red">Failed to load (support account required).</Text>;
	}
	const rows = query.data ?? [];
	if (rows.length === 0) {
		return <Text c="dark.0">No rows for this bank account.</Text>;
	}
	return (
		<Flex direction="column" gap="0.5rem">
			<Flex justify="space-between" align="center">
				<Text size="sm" c="dark.0">
					{rows.length} row{rows.length === 1 ? '' : 's'} (newest first)
				</Text>
				<Button variant="subtle" size="xs" onClick={() => void query.refetch()}>
					Refresh
				</Button>
			</Flex>
			{rows.map((row, index) => (
				<Paper key={String(row.id ?? index)} withBorder p="0.75rem">
					<Flex justify="space-between" gap="0.5rem">
						<Text fw={600} size="sm">
							{rowHeadline(row)}
						</Text>
						<Text size="sm" c="dark.0">
							{row.createdAt ? new Date(String(row.createdAt)).toLocaleString() : ''}
						</Text>
					</Flex>
					<pre
						style={{
							margin: '0.5rem 0 0',
							fontSize: '0.75rem',
							whiteSpace: 'pre-wrap',
							wordBreak: 'break-all',
							lineHeight: 1.5,
						}}
					>
						{JSON.stringify(row, null, 2)}
					</pre>
				</Paper>
			))}
		</Flex>
	);
};

interface Props {
	bankAccountId: string;
	opened: boolean;
	onClose: () => void;
}

export const BankAccountLifecycleDebugDrawer = ({ bankAccountId, opened, onClose }: Props) => {
	const [activeTab, setActiveTab] = useState<DebugTab>('logs');

	const tabsConfig: MantineTabProps<DebugTab>[] = [
		{ label: 'Validation logs', value: 'logs' },
		{ label: 'State transitions', value: 'transitions' },
	];

	const logsQuery = useQuery({
		queryKey: ['bankAccountLifecycleDebug', 'logs', bankAccountId],
		queryFn: () =>
			fetchRows(
				API_PATHS.BANK_ACCOUNT_LIFECYCLE_DEBUG_ID_VERIFICATION_LOGS_GET,
				bankAccountId
			),
		enabled: opened && activeTab === 'logs' && !!bankAccountId,
	});
	const transitionsQuery = useQuery({
		queryKey: ['bankAccountLifecycleDebug', 'transitions', bankAccountId],
		queryFn: () =>
			fetchRows(
				API_PATHS.BANK_ACCOUNT_LIFECYCLE_DEBUG_ID_STATE_TRANSITIONS_GET,
				bankAccountId
			),
		enabled: opened && activeTab === 'transitions' && !!bankAccountId,
	});

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			title={`Bank account lifecycle — ${bankAccountId}`}
			styles={{ body: { height: 'calc(100% - 60px)', overflow: 'auto' } }}
		>
			<MantineTabs
				tabs={tabsConfig}
				panels={[
					{
						value: 'logs',
						content: (
							<Box p="0.75rem">
								<RowList query={logsQuery} />
							</Box>
						),
					},
					{
						value: 'transitions',
						content: (
							<Box p="0.75rem">
								<RowList query={transitionsQuery} />
							</Box>
						),
					},
				]}
				value={activeTab}
				onTabChange={(value) => {
					if (isDebugTab(value)) {
						setActiveTab(value);
					}
				}}
			/>
		</Drawer>
	);
};
